import React from 'react'
import { Box } from '@mui/material'

import {
  BASELINE_HEIGHT,
  CHART_CANVAS_WIDTH,
  CHART_HEIGHT,
  CURRENT_BAR_WIDTH,
  PREVIOUS_BAR_WIDTH
} from './comparisonBarChartConstants'
import { AnimatedCurrentBar, AnimatedPreviousBar, MovementBars } from './ComparisonBarChartBars'
import { INTERAX_COLORS } from '../../../../constants/colors'
import ComparisonBarChartLabels from './ComparisonBarChartLabels'
import { TrendPoint } from './comparisonBarChartMath'

interface ComparisonBarChartCanvasProps {
  trendPoints: TrendPoint[]
  shouldAnimate: boolean
  movementHeights: number[]
  movementLeft: number
  hasMovementBars: boolean
  previousValue: number
  currentValue: number
  maxValue: number
  previousCenterX: number
  currentCenterX: number
  previousLabel: string
  currentLabel: string
  previousAriaLabel: string
  currentAriaLabel: string
}

const ComparisonBarChartCanvas: React.FC<ComparisonBarChartCanvasProps> = ({
  trendPoints,
  shouldAnimate,
  movementHeights,
  movementLeft,
  hasMovementBars,
  previousValue,
  currentValue,
  maxValue,
  previousCenterX,
  currentCenterX,
  previousLabel,
  currentLabel,
  previousAriaLabel,
  currentAriaLabel
}) => {
  return (
    <Box
      sx={{
        position: 'relative',
        width: `${CHART_CANVAS_WIDTH}px`,
        height: `${CHART_HEIGHT}px`
      }}
    >
      {trendPoints.length > 0 && (
        <Box
          component="svg"
          viewBox={`0 0 ${CHART_CANVAS_WIDTH} ${CHART_HEIGHT}`}
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none'
          }}
        >
          <path
            d={`M ${trendPoints[0].x} ${trendPoints[0].y} C ${trendPoints[0].x + 24} ${trendPoints[0].y}, ${trendPoints[1].x - 24} ${trendPoints[1].y}, ${trendPoints[1].x} ${trendPoints[1].y} S ${trendPoints[2].x - 24} ${trendPoints[2].y}, ${trendPoints[2].x} ${trendPoints[2].y}`}
            fill="none"
            stroke={INTERAX_COLORS.sun}
            strokeWidth="2"
            strokeDasharray="4 6"
            style={{
              opacity: shouldAnimate ? 1 : 0,
              transition: 'opacity 700ms ease-out 150ms'
            }}
          />
          {trendPoints.map((point, index) => (
            <circle
              key={`trend-point-${index}`}
              cx={point.x}
              cy={point.y}
              r={index === 1 ? 3 : 4}
              fill={INTERAX_COLORS.white}
              stroke={INTERAX_COLORS.sun}
              strokeWidth="2"
              style={{
                opacity: shouldAnimate ? 1 : 0,
                transition: `opacity 600ms ease-out ${200 + index * 120}ms`
              }}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: `${BASELINE_HEIGHT}px`,
          borderBottom: '1px solid rgba(255, 255, 255, 0.25)'
        }}
      />

      {hasMovementBars && <MovementBars heights={movementHeights} animate={shouldAnimate} left={movementLeft} />}

      {previousValue > 0 && (
        <AnimatedPreviousBar
          value={previousValue}
          maxValue={maxValue}
          animate={shouldAnimate}
          width={PREVIOUS_BAR_WIDTH}
          ariaLabel={previousAriaLabel}
        />
      )}

      <AnimatedCurrentBar
        value={currentValue}
        maxValue={maxValue}
        animate={shouldAnimate}
        width={CURRENT_BAR_WIDTH}
        ariaLabel={currentAriaLabel}
      />

      <ComparisonBarChartLabels
        previousLabel={previousLabel}
        currentLabel={currentLabel}
        previousCenterX={previousCenterX}
        currentCenterX={currentCenterX}
      />
    </Box>
  )
}

export default ComparisonBarChartCanvas
